import type { Tone } from "@amtana/design-system";
import type { ExportFormat, ExportStatus } from "@amtana/export-contract";
import type { Catalogs, PlainKey, Translation } from "@amtana/i18n/catalog";

// The export screens' words, in English and Spanish: one key per string, read
// through `useExportCopy` (behavior/use-export-copy.ts).

export type ExportCopyKey = keyof typeof EN;
export type ExportCatalog = typeof EN;

const EN = {
  exportsScreenTitle: "Exports · {product}",
  downloadScreenTitle: "Download · {product}",
  statusPending: "Pending",
  statusProcessing: "Processing",
  statusCompleted: "Completed",
  statusFailed: "Failed",
  formatCsv: "CSV",
  formatExcel: "Excel",
  formatSmartlead: "Smartlead",
  sourceAnalysis: "Analysis",
  sourceList: "Filter",
  sourceProspects: "Prospects",
  breadcrumbProspects: "Prospects",
  breadcrumbExports: "Exports",
  exportsTitle: "Your exports",
  exportsDescription: "Check all the exports you have made",
  exportsTableLabel: "Exports",
  emptyTitle: "No exports yet",
  emptyDescription: "Create your first export to see data here.",
  columnSource: "Source",
  columnId: "ID",
  columnStatus: "Status",
  columnCreated: "Created at",
  columnCompleted: "Completed at",
  columnTotalRows: "Total rows",
  columnActions: "Actions",
  actionsLabel: "Actions for export {id}",
  actionDownload: "Download",
  actionCopyUrl: "Copy URL",
  actionRetry: "Retry",
  urlCopied: "URL copied to clipboard",
  couldNotCopyUrl: "Could not copy the URL",
  retryQueued: "Export retry has been queued",
  couldNotRetry: "Could not retry the export",
  processingTitle: "Your export is being processed",
  processingDescription: "Please wait a moment.",
  processingSpinner: "Processing…",
  failedTitle: "We couldn't prepare your export",
  failedDescription: "Retry it from your exports.",
  readyTitle: "Congratulations!",
  readyDescription: "Your export is ready, click the button below to download it.",
  downloadButton: "DOWNLOAD",
  // What a person leaving can take from the Terms gate (ADR-032, S37.07).
  takeExportsTitle: "Your exports",
  takeExportDownload: "Download",
  takeExportFailed: "We couldn't download this export",
  downloadDetails: "ID {id} · {rows}",
  rowCount: { one: "1 row", other: "{count} rows" },
  dialogTitle: "Export prospects to {format}",
  dialogDescription: "Review exactly what will be included before downloading.",
  cancel: "Cancel",
  exportStarted: "Export started",
  exportStartedHint: "It will be ready in a moment.",
  couldNotStart: "Could not start the export",
  exportRows: { one: "Export 1 row", other: "Export {count} rows" },
  formatLabel: "Format",
  summary: "You're about to export {prospects} as a {format} file. Here's a quick look.",
  prospectCount: { one: "1 prospect", other: "{count} prospects" },
  differentListTitle: "Want a different list?",
  differentListDescription:
    "Close this and tweak the Filters or Columns on the table. Your export always matches what you see there.",
  previewLabel: "Export preview",
  previewName: "Name",
  previewEmail: "Email",
  previewPhone: "Phone",
  previewFooter: "Showing first {shown} of {total} rows",
} as const;

const ES: Translation<ExportCatalog> = {
  exportsScreenTitle: "Exportaciones · {product}",
  downloadScreenTitle: "Descarga · {product}",
  statusPending: "Pendiente",
  statusProcessing: "Procesando",
  statusCompleted: "Completada",
  statusFailed: "Fallida",
  formatCsv: "CSV",
  formatExcel: "Excel",
  formatSmartlead: "Smartlead",
  sourceAnalysis: "Análisis",
  sourceList: "Filtro",
  sourceProspects: "Prospectos",
  breadcrumbProspects: "Prospectos",
  breadcrumbExports: "Exportaciones",
  exportsTitle: "Tus exportaciones",
  exportsDescription: "Revisa todas las exportaciones que has hecho",
  exportsTableLabel: "Exportaciones",
  emptyTitle: "Todavía no hay exportaciones",
  emptyDescription: "Crea tu primera exportación para ver datos aquí.",
  columnSource: "Origen",
  columnId: "ID",
  columnStatus: "Estado",
  columnCreated: "Creada el",
  columnCompleted: "Completada el",
  columnTotalRows: "Total de filas",
  columnActions: "Acciones",
  actionsLabel: "Acciones de la exportación {id}",
  actionDownload: "Descargar",
  actionCopyUrl: "Copiar URL",
  actionRetry: "Reintentar",
  urlCopied: "URL copiada al portapapeles",
  couldNotCopyUrl: "No se pudo copiar la URL",
  retryQueued: "El reintento de la exportación está en cola",
  couldNotRetry: "No se pudo reintentar la exportación",
  processingTitle: "Tu exportación se está procesando",
  processingDescription: "Espera un momento.",
  processingSpinner: "Procesando…",
  failedTitle: "No pudimos preparar tu exportación",
  failedDescription: "Reinténtala desde tus exportaciones.",
  readyTitle: "¡Felicidades!",
  readyDescription: "Tu exportación está lista. Haz clic en el botón para descargarla.",
  downloadButton: "DESCARGAR",
  takeExportsTitle: "Tus exportaciones",
  takeExportDownload: "Descargar",
  takeExportFailed: "No pudimos descargar esta exportación",
  downloadDetails: "ID {id} · {rows}",
  rowCount: { one: "1 fila", other: "{count} filas" },
  dialogTitle: "Exportar prospectos a {format}",
  dialogDescription: "Revisa exactamente qué se incluirá antes de descargar.",
  cancel: "Cancelar",
  exportStarted: "Exportación iniciada",
  exportStartedHint: "Estará lista en un momento.",
  couldNotStart: "No se pudo iniciar la exportación",
  exportRows: { one: "Exportar 1 fila", other: "Exportar {count} filas" },
  formatLabel: "Formato",
  summary: "Estás a punto de exportar {prospects} como un archivo {format}. Aquí tienes un vistazo.",
  prospectCount: { one: "1 prospecto", other: "{count} prospectos" },
  differentListTitle: "¿Quieres otra lista?",
  differentListDescription:
    "Cierra esto y ajusta los filtros o las columnas de la tabla. Tu exportación siempre coincide con lo que ves ahí.",
  previewLabel: "Vista previa de la exportación",
  previewName: "Nombre",
  previewEmail: "Correo",
  previewPhone: "Teléfono",
  previewFooter: "Mostrando las primeras {shown} de {total} filas",
};

export const EXPORT_CATALOGS: Catalogs<ExportCatalog> = { en: EN, es: ES };

export const STATUS: Readonly<Record<ExportStatus, { label: PlainKey<ExportCatalog>; tone: Tone }>> = {
  pending: { label: "statusPending", tone: "neutral" },
  processing: { label: "statusProcessing", tone: "info" },
  completed: { label: "statusCompleted", tone: "success" },
  failed: { label: "statusFailed", tone: "danger" },
};

export const FORMAT_LABEL: Readonly<Record<ExportFormat, PlainKey<ExportCatalog>>> = {
  csv: "formatCsv",
  excel: "formatExcel",
  smartlead: "formatSmartlead",
};

export const SOURCE_LABEL = {
  analysis: "sourceAnalysis",
  list: "sourceList",
  prospects: "sourceProspects",
} as const satisfies Readonly<Record<string, PlainKey<ExportCatalog>>>;
